import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { type ProjectDetailsMeta } from "@tokenterminal/tt-types"
import { type GranularityType } from "@tokenterminal/ui/Chart/utils/groupDataBasedOnGranularity"
import { useMemo, useState } from "react"
import { type MetricConfiguration } from "utils/trpc"

import { useAvailableEcosystemMetricIds } from "../hooks/useAvailableEcosystemMetricIds"
import { useAvailableMetricIdsForProject } from "../hooks/useAvailableMetricIdsForProject"
import { useMetricsConfigurationList } from "../hooks/useMetricsConfigurationList"
import { MetricsDropdown } from "../MetricsDropdown"
import { ComparisonChart } from "./ComparisonChart"

export const ComparisonChartWithMetricDropdown = ({
  data,
  metric1Default,
  productId,
}: {
  data: ProjectDetailsMeta
  metric1Default: string
  productId?: string
}) => {
  const [metric1Slug, setMetric1Slug] = useState<string>(metric1Default)

  const { data: availableMetricIdsForProject = [] } =
    useAvailableMetricIdsForProject(data.api_id, productId)
  const { data: ecosystemMetricIds = [] } = useAvailableEcosystemMetricIds()
  const ecosystemMetricSlugs = ecosystemMetricIds.map((id) =>
    id.replace(/_/g, "-")
  )
  const { data: metricConfigurations = [] } = useMetricsConfigurationList()

  const availableMetrics: MetricConfiguration[] = useMemo(() => {
    const items = availableMetricIdsForProject
      .map((id) =>
        metricConfigurations.find((metric) => metric.metric_id === id)
      )
      .filter(Boolean) as MetricConfiguration[]

    return items
  }, [availableMetricIdsForProject, metricConfigurations])

  const [interval, setInterval] = useState<Interval>("365d")
  const [granularity, setGranularity] = useState<GranularityType>("week")

  return (
    <ComparisonChart
      data_id={data.data_id}
      data={data}
      metric1Slug={metric1Slug}
      headingSlot={({ comparison }) => {
        const shouldDisableEcosystemMetrics =
          comparison?.type === "market_sector"

        return (
          <MetricsDropdown
            availableMetrics={availableMetrics || []}
            selectedMetric={metric1Slug || undefined}
            onMetricChange={setMetric1Slug}
            disabledKeys={
              shouldDisableEcosystemMetrics ? ecosystemMetricSlugs : []
            }
            buttonProps={{
              className: "!text-lg tracking-tight -ml-2.5 !px-2",
              variant: "ghost",
            }}
          />
        )
      }}
      granularity={granularity}
      interval={interval}
      setGranularity={setGranularity}
      setInterval={setInterval}
      productId={productId}
      hideSheetsFormula={!!productId}
    />
  )
}
